// Light mode colors (Billda brand theme)
export const colors = {
  primary: {
    main: '#293038', // Billda brand blue-gray (from logo) — high contrast on white
    light: '#4A5C6E',
    dark: '#1A1F24',
    contrastText: '#ffffff',
    50: '#E8EAED',
    100: '#C5CBD2',
    200: '#9FA8B4',
    300: '#798596',
    400: '#5C6A7F',
    500: '#293038',
    600: '#374451',
    700: '#293038',
    800: '#1E252B',
    900: '#1A1F24',
  },
  secondary: {
    main: '#C17F00', // Billda amber/gold accent
    light: '#D99A00',
    dark: '#A36A00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
    50: '#FEF8E5',
    100: '#FDEEBE',
    200: '#FCE392',
    300: '#FBD866',
    400: '#F0B726',
    500: '#E8A500',
    600: '#D99D00',
    700: '#C99000',
    800: '#B38200',
    900: '#9D7300',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
    contrastText: '#ffffff',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    A100: '#f5f5f5',
    A200: '#eeeeee',
    A400: '#bdbdbd',
    A700: '#616161',
  },
  background: {
    default: '#f5f5f7',
    paper: '#FFFFFF',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.7)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Dark mode colors (Billda brand theme for dark backgrounds)
export const darkColors = {
  primary: {
    main: '#F0B726', // Slightly brighter yellow for dark mode visibility
    light: '#F7C333',
    dark: '#E8A500',
    contrastText: 'rgba(0, 0, 0, 0.87)', // Dark text on yellow
    50: '#FEF8E5',
    100: '#FDEEBE',
    200: '#FCE392',
    300: '#FBD866',
    400: '#F0B726',
    500: '#E8A500',
    600: '#D99D00',
    700: '#C99000',
    800: '#B38200',
    900: '#9D7300',
  },
  secondary: {
    main: '#232A31', // Mid-tone brand blue for dark mode header
    light: '#BCC9D9',
    dark: '#374451',
    contrastText: '#FFFFFF',
    50: '#1A1F24',
    100: '#1E252B',
    200: '#293038',
    300: '#374451',
    400: '#4A5C6E',
    500: '#9FB0C6',
    600: '#B0BFD2',
    700: '#BCC9D9',
    800: '#CDD8E3',
    900: '#DFE6ED',
  },
  error: {
    main: '#ef5350',
    light: '#e57373',
    dark: '#c62828',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ffa726',
    light: '#ffb960',
    dark: '#f57c00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  info: {
    main: '#29b6f6',
    light: '#4fc3f7',
    dark: '#0288d1',
    contrastText: '#ffffff',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#1a1a1a',
    100: '#2e2e2e',
    200: '#424242',
    300: '#565656',
    400: '#6a6a6a',
    500: '#7e7e7e',
    600: '#929292',
    700: '#a6a6a6',
    800: '#bababa',
    900: '#cecece',
    A100: '#2e2e2e',
    A200: '#424242',
    A400: '#6a6a6a',
    A700: '#a6a6a6',
  },
  background: {
    default: '#1E2125', // Content area base — warm-neutral blue-gray
    paper: '#272B30', // Elevated surfaces — panels, popovers, cards
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.95)', // Brighter for better readability
    secondary: 'rgba(255, 255, 255, 0.78)', // 0.78 opacity — WCAG AA on all dark surfaces
    disabled: 'rgba(255, 255, 255, 0.45)', // Slightly brighter disabled text
  },
};

// Shell surface — sidebar, top bar, area behind the rounded content inset
// One step below background.default to create a 3-level hierarchy (shell → content → elevated)
export const DARK_SHELL_SURFACE = '#191C20' as const;
export const LIGHT_SHELL_SURFACE = '#E4E5E9' as const;

// Semantic alias for the app header background — used by SitesListHeader and CommonScreenHeader
// Change this one value to retheme all app headers at once
export const APP_HEADER_BG = '#293038' as const;   // Billda brand blue-gray — always dark regardless of theme mode
export const APP_HEADER_COLOR = '#ffffff' as const; // White text on dark header